import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import './Analytics.css';

const MenuEngineering = () => {
  const [menuData, setMenuData] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyzeMenu();
  }, []);

  const analyzeMenu = async () => {
    try {
      const menuSnapshot = await getDocs(collection(db, 'menu_items'));
      const ordersSnapshot = await getDocs(query(collection(db, 'orders'), where('status', '==', 'completed')));
      
      const menuItems = menuSnapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
      const orders = ordersSnapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
      
      const analysis = analyzeMenuItems(menuItems, orders);
      setMenuData(analysis);
      categorizeItems(analysis);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const analyzeMenuItems = (menuItems, orders) => {
    return menuItems.map(item => {
      const itemOrders = orders.flatMap(o => o.items?.filter(i => i.name === item.name) || []);
      const quantity = itemOrders.reduce((sum, i) => sum + (i.quantity || 0), 0);
      const revenue = itemOrders.reduce((sum, i) => sum + ((i.price || 0) * (i.quantity || 0)), 0);
      const profit = revenue * 0.6; // Assuming 60% profit margin
      
      return {
        name: item.name,
        price: item.price,
        quantity,
        revenue,
        profit,
        category: item.category
      };
    }).filter(item => item.quantity > 0);
  };

  const categorizeItems = (items) => {
    const avgQuantity = items.reduce((sum, i) => sum + i.quantity, 0) / items.length;
    const avgProfit = items.reduce((sum, i) => sum + i.profit, 0) / items.length;
    
    const stars = items.filter(i => i.quantity >= avgQuantity && i.profit >= avgProfit);
    const plowhorses = items.filter(i => i.quantity >= avgQuantity && i.profit < avgProfit);
    const puzzles = items.filter(i => i.quantity < avgQuantity && i.profit >= avgProfit);
    const dogs = items.filter(i => i.quantity < avgQuantity && i.profit < avgProfit);
    
    setCategories({stars, plowhorses, puzzles, dogs});
  };

  const getQuadrantColor = (item) => {
    const avgQty = menuData.reduce((sum, i) => sum + i.quantity, 0) / menuData.length;
    const avgProfit = menuData.reduce((sum, i) => sum + i.profit, 0) / menuData.length;
    
    if (item.quantity >= avgQty && item.profit >= avgProfit) return '#2ecc71'; // Stars
    if (item.quantity >= avgQty && item.profit < avgProfit) return '#3498db'; // Plowhorses
    if (item.quantity < avgQty && item.profit >= avgProfit) return '#f39c12'; // Puzzles
    return '#e74c3c'; // Dogs
  };

  if (loading) return <div className="loading-container"><div className="spinner-large"></div></div>;

  return (
    <div className="menu-engineering-page">
      <div className="page-header">
        <h1>🍽️ Menu Engineering</h1>
        <p>Menu Performance Matrix - Stars, Plowhorses, Puzzles & Dogs</p>
      </div>

      <div className="metrics-grid">
        <div className="metric-card"><div className="metric-icon" style={{background: '#2ecc71'}}>⭐</div><div className="metric-content"><p className="metric-label">Stars</p><p className="metric-value">{categories.stars?.length || 0}</p><p className="metric-subtitle">High popularity, High profit</p></div></div>
        <div className="metric-card"><div className="metric-icon" style={{background: '#3498db'}}>🐴</div><div className="metric-content"><p className="metric-label">Plowhorses</p><p className="metric-value">{categories.plowhorses?.length || 0}</p><p className="metric-subtitle">High popularity, Low profit</p></div></div>
        <div className="metric-card"><div className="metric-icon" style={{background: '#f39c12'}}>🧩</div><div className="metric-content"><p className="metric-label">Puzzles</p><p className="metric-value">{categories.puzzles?.length || 0}</p><p className="metric-subtitle">Low popularity, High profit</p></div></div>
        <div className="metric-card"><div className="metric-icon" style={{background: '#e74c3c'}}>🐕</div><div className="metric-content"><p className="metric-label">Dogs</p><p className="metric-value">{categories.dogs?.length || 0}</p><p className="metric-subtitle">Low popularity, Low profit</p></div></div>
      </div>

      <div className="chart-container">
        <h3>Menu Performance Matrix</h3>
        <ResponsiveContainer width="100%" height={500}>
          <ScatterChart margin={{top: 20, right: 20, bottom: 20, left: 20}}>
            <CartesianGrid />
            <XAxis type="number" dataKey="quantity" name="Popularity" label={{value: 'Popularity (Orders)', position: 'bottom'}} />
            <YAxis type="number" dataKey="profit" name="Profit" label={{value: 'Profit (₹)', angle: -90, position: 'left'}} tickFormatter={(value) => `₹${value.toFixed(0)}`} />
            <Tooltip cursor={{strokeDasharray: '3 3'}} formatter={(value, name) => name === 'profit' ? `₹${value.toFixed(2)}` : value} />
            <Legend />
            <Scatter name="Menu Items" data={menuData}>
              {menuData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getQuadrantColor(entry)} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      <div className="insights-section">
        <h3>📊 Recommendations</h3>
        <div className="insights-grid">
          <div className="insight-card"><strong>⭐ Stars:</strong><p>Promote heavily, maintain quality, prime menu placement.</p></div>
          <div className="insight-card"><strong>🐴 Plowhorses:</strong><p>Increase prices slightly or reduce costs to improve margins.</p></div>
          <div className="insight-card"><strong>🧩 Puzzles:</strong><p>Increase visibility through promotions and better positioning.</p></div>
          <div className="insight-card"><strong>🐕 Dogs:</strong><p>Consider removing or repositioning. Evaluate if worth keeping.</p></div>
        </div>
      </div>
    </div>
  );
};

export default MenuEngineering;
